import { classes, on } from '~/helpers';
import { HTMLCustomElement } from '~/html-custom-element';
import { isFromBreakpoint } from '~/breakpoints';
import { animate } from '~/animations';

class ProductGallery extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    if (this.targets.thumbnail) {
      this.on('click', this.targets.thumbnail.all, (event) => {
        this.goToIndex(event.currentTarget.dataset.index0);
      });
    }
  }

  /**
   * Navigates to the item index.
   * @param {String} index0 - The index to go to.
   */
  goToIndex(index0) {
    if (!this.targets.item) return;

    this.targets.item.all.forEach((element) => {
      element.classList.toggle(classes.active, element.dataset.index0 === index0);
    });

    this.resetZoom();
  }

  /**
   * Resets the zoom states.
   */
  resetZoom() {
    [...this.querySelectorAll('product-image')].forEach((element) => {
      element.zoomOut();
    });
  }
}

class ProductImage extends HTMLCustomElement {
  removeMouseMoveEvent = null;

  get zoomElement() {
    return this.querySelector('img');
  }

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.on('click', this, () => {
      if (this.classList.contains(classes.zoomed)) {
        this.zoomOut();
        return;
      }

      this.zoomIn();
    });
  }

  /**
   * Zooms into the zoom element.
   * @param {MouseEvent} mouseEvent - The mouse event.
   */
  zoomIn(mouseEvent) {
    if (!isFromBreakpoint('large')) return;

    this.classList.add(classes.zoomed);

    if (this.hasAttribute('data-custom-cursor')) {
      this.dataset.customCursor = theme.strings.sections.main_product.gallery.zoom_out;
    }

    if (mouseEvent) {
      this.handleMouseMove(mouseEvent, this);
    }

    if (!this.removeMouseMoveEvent) {
      this.removeMouseMoveEvent = on('mousemove', document, (event) => this.handleMouseMove(event, this));
    }
  }

  /**
   * Zooms out of the zoom element.
   */
  zoomOut() {
    this.classList.remove(classes.zoomed);

    if (this.removeMouseMoveEvent) {
      this.removeMouseMoveEvent = this.removeMouseMoveEvent();
    }

    if (this.hasAttribute('data-custom-cursor')) {
      this.dataset.customCursor = theme.strings.sections.main_product.gallery.zoom_in;
    }

    /**
     * Reset the zoom element's styles.
     */
    this.applyZoomSyles(0, 0, 1);
  }

  /**
   * Applies the zoom styles to the zoom element.
   * @param {Number} x - The x-axis position.
   * @param {Number} y - The y-axis position.
   * @param {Number} scale - The zoom scale.
   */
  applyZoomSyles(x = 0, y = 0, scale = 1) {
    if (this.zoomElement) {
      this.zoomElement.style.transformOrigin = `${x}% ${y}%`;
      this.zoomElement.style.scale = scale;
    }
  }

  /**
   * Handles the document mouse move event.
   * @param {MouseEvent} event - The mouse event.
   * @param {HTMLElement} container - The element container.
   */
  handleMouseMove(event) {
    const { left, top, width, height } = this.getBoundingClientRect();
    const x = ((event.clientX - left) / width) * 100;
    const y = ((event.clientY - top) / height) * 100;

    if (this.isMouseInside(event)) {
      this.applyZoomSyles(x, y, 2);
    }
  }

  /**
   * Returns if the mouse is inside the container element.
   * @param {MouseEvent} mouseEvent - The mouse event.
   * @param {HTMLElement} element - The container element.
   * @returns {Boolean}
   */
  isMouseInside(mouseEvent) {
    const rect = this.getBoundingClientRect();

    return (
      mouseEvent.clientX >= rect.left &&
      mouseEvent.clientX <= rect.right &&
      mouseEvent.clientY >= rect.top &&
      mouseEvent.clientY <= rect.bottom
    );
  }
}

if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);
if (!customElements.get('product-image')) customElements.define('product-image', ProductImage);
